import React, { useMemo, useState } from "react";
import { buildSubmission } from "./FormSubmission";

const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());

function FormNotice({ status, message }) {
  if (!status) return null;
  return (
    <div
      className={`form-notice ${status === "success" ? "is-success" : "is-error"}`}
      role="status"
      aria-live="polite"
      style={{ marginTop: "2rem", color: "#EA0029"}} 
    >
      {message}
    </div>
  );
}
export const VisitForm = ({ onSubmitData }) => {
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [projectDetails, setProjectDetails] = useState("");

  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState({ state: "idle", message: "" }); // idle | loading | success | error

  const requiredKeys = ["name", "company", "email", "message"];

  const submission = useMemo(
    () =>
      buildSubmission({
        kind: "visit",
        fields: {
          name,
          company,
          email,
          phone,
          message: projectDetails, // ✅ keep same field mapping
        },
        meta: { page: window.location.pathname },
      }),
    [name, company, email, phone, projectDetails]
  );

  const baseValidation = submission.validate(requiredKeys);
  const emailOk = email ? isEmail(email) : false;

  // ✅ same behavior as your ConsultationForm right now (blank messages)
  const errors = {
    name: baseValidation.missing.includes("name") ? "" : "",
    company: baseValidation.missing.includes("company") ? "" : "",
    email: baseValidation.missing.includes("email") ? "" : !emailOk ? "" : "",
    message: baseValidation.missing.includes("message") ? "" : "",
  };

  const canSubmit = status.state !== "loading" && baseValidation.ok && emailOk;

  const showInvalidEmailNotice = !!email && !emailOk;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, company: true, email: true, message: true });

    if (!canSubmit) {
      setStatus({ state: "error", message: "" });
      return;
    }

    try {
      setStatus({ state: "loading", message: "" });

      if (typeof onSubmitData === "function") await onSubmitData(submission);

      setStatus({ state: "success", message: "" });
      setTimeout(() => setStatus({ state: "idle", message: "" }), 2000);

      // clear after success (same behavior as ConsultationForm)
      setName("");
      setCompany("");
      setEmail("");
      setPhone("");
      setProjectDetails("");
      setTouched({});
    } catch (err) {
      setStatus({
        state: "error",
        message: err?.message || "Something went wrong. Please try again.",
      });
    }
  };

  const fieldClass = (key) => (touched[key] && errors[key] ? "has-error" : "");

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="slope-contact-form">
        <div className="fields-wrapper">
          <div className="input-contact">
            <span className={`wpcf7-form-control-wrap name-field ${fieldClass("name")}`}>
              <input
                value={name}
                onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                onChange={(e) => setName(e.target.value)}
                type="text"
                name="name"
                placeholder="Your name *"
                aria-invalid={!!(touched.name && errors.name)}
              />
              {touched.name && errors.name ? <small className="field-error">{errors.name}</small> : null}
            </span>

            <span className={`wpcf7-form-control-wrap company-field ${fieldClass("company")}`}>
              <input
                value={company}
                onBlur={() => setTouched((t) => ({ ...t, company: true }))}
                onChange={(e) => setCompany(e.target.value)}
                type="text"
                name="company"
                placeholder="Your company *"
                aria-invalid={!!(touched.company && errors.company)}
              />
              {touched.company && errors.company ? <small className="field-error">{errors.company}</small> : null}
            </span>

            <span className={`wpcf7-form-control-wrap email-field ${fieldClass("email")}`}>
              <input
                value={email}
                onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                name="email"
                placeholder="Email address *"
                aria-invalid={!!(touched.email && errors.email)}
              />
              {touched.email && errors.email ? <small className="field-error">{errors.email}</small> : null}
            </span>

            <span className="wpcf7-form-control-wrap number-field">
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                type="tel"
                name="phone"
                placeholder="Phone"
              />
            </span>
          </div>

          <div className="textarea-contact">
            <span className={`wpcf7-form-control-wrap message-field ${fieldClass("message")}`}>
              <textarea
                value={projectDetails}
                onBlur={() => setTouched((t) => ({ ...t, message: true }))}
                onChange={(e) => setProjectDetails(e.target.value)}
                name="message"
                cols="40"
                rows="10"
                placeholder="Write your message *"
                aria-invalid={!!(touched.message && errors.message)}
              />
              {touched.message && errors.message ? <small className="field-error">{errors.message}</small> : null}
            </span>
          </div>
        </div>

        <div className="submit-contact">
          <input
            type="submit"
            value={status.state === "loading" ? "Sending..." : status.state === "success" ? "Sent ✓" : "Submit"}
            disabled={!canSubmit}
            style={{ cursor: canSubmit ? "pointer" : "not-allowed" }}
          />
          {showInvalidEmailNotice ? (
            <span style={{ marginLeft: "1rem", color: "#EA0029" }}>Email is invalid.</span>
          ) : null}
          <FormNotice
            status={status.state === "success" ? "success" : status.state === "error" ? "error" : ""}
            message={status.message}
          />
        </div>
      </div>
    </form>
  );
};
